import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@components/Button";
import { BinIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useRemoveCustomTransactionCategoryMutation } from "@services/budget/mutations";
import {
  RemoveBudgetTransactionCategoryType,
  type IGetCustomTransactionCategoryResponse,
} from "@services/budget/types";

interface IRemoveBudgetTransactionCategoryModalProps extends IUseModalProps {
  transactionCategoryData: IGetCustomTransactionCategoryResponse;
}

export const RemoveBudgetTransactionCategoryModal: React.FC<
  IRemoveBudgetTransactionCategoryModalProps
> = (props) => {
  const { transactionCategoryData, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();

  const { mutateAsync: removeCustomTransactionCategory, isPending } =
    useRemoveCustomTransactionCategoryMutation();

  const { toastApiError } = useApiErrorUtils();

  const removeCategory = async () => {
    await removeCustomTransactionCategory({
      transactionCategoryId: transactionCategoryData.transactionCategoryId,
      removeType: RemoveBudgetTransactionCategoryType.NoAction,
    })
      .then(modal.close)
      .catch(toastApiError);
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{
        icon: BinIcon,
        color: "red",
      }}
      title={t("budget.removeBudgetTransactionCategoryModal.title", {
        name: transactionCategoryData.categoryName,
      })}
      subtitle={t("budget.removeBudgetTransactionCategoryModal.subtitle", {
        name: transactionCategoryData.categoryName,
      })}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" className="w-full" onClick={modal.close}>
            {t("budget.removeBudgetTransactionCategoryModal.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={removeCategory}
            isLoading={isPending}
          >
            {t("budget.removeBudgetTransactionCategoryModal.submit")}
          </Button>
        </React.Fragment>
      }
    ></Modal>
  );
};
